/**
 * Builds the command list the palette renders. Pulls live data from
 * existing React Query / Zustand sources — projects, recent sessions,
 * the project store's active context — and wraps navigation + action
 * intents into stable `Command` objects.
 *
 * The list is recomputed on every render of the palette, but the inputs
 * change rarely (project list, sessions list) so React Query's cache
 * keeps it cheap. Memoisation lives in the hook to avoid the palette
 * re-running fuzzy-scoring against an entirely new array on every input
 * keystroke.
 */
import { useShortcutsDialog } from "@components/KeyboardShortcutsDialog";
import { useSessions } from "@features/history";
import { useProjects } from "@features/projects";
import NewChatIcon from "@mui/icons-material/AddRounded";
import BoltIcon from "@mui/icons-material/BoltRounded";
import ChatIcon from "@mui/icons-material/ChatBubbleOutlineRounded";
import DraftIcon from "@mui/icons-material/EditNoteRounded";
import HistoryIcon from "@mui/icons-material/HistoryRounded";
import ProjectsIcon from "@mui/icons-material/HubRounded";
import KeyboardIcon from "@mui/icons-material/KeyboardRounded";
import LogoutIcon from "@mui/icons-material/LogoutRounded";
import PersonIcon from "@mui/icons-material/PersonOutlineRounded";
import SettingsIcon from "@mui/icons-material/SettingsOutlined";
import UploadIcon from "@mui/icons-material/UploadFileOutlined";
import { useDraftStore } from "@stores/draftStore";
import { useOrgStore } from "@stores/orgStore";
import { useNavigate } from "@tanstack/react-router";
import { useMemo } from "react";

import type { Command } from "./commandTypes";

interface UseCommandsArgs {
  /** Fired by every command's action so the palette closes after
   *  navigating / acting. Wired by the palette itself. */
  onClose: () => void;
}

export function useCommands({ onClose }: UseCommandsArgs): Command[] {
  const navigate = useNavigate();
  const activeOrgId = useOrgStore((s) => s.activeOrgId);
  const openDraftPanel = useDraftStore((s) => s.openPanel);
  const activeSnapshotId = useDraftStore((s) => s.activeSnapshotId);
  const openShortcuts = useShortcutsDialog((s) => s.openShortcuts);
  const { data: projects } = useProjects(activeOrgId ?? "personal");
  const { data: sessions } = useSessions(activeOrgId);

  return useMemo(() => {
    const cmds: Command[] = [];
    const close = () => onClose();

    // ── Navigate ─────────────────────────────────────────────────────
    cmds.push(
      {
        id: "nav-chat",
        label: "Go to Chat",
        category: "navigate",
        icon: <ChatIcon fontSize="small" />,
        action: () => {
          void navigate({ to: "/chat" });
          close();
        },
        keywords: ["chat", "talk", "agent"],
      },
      {
        id: "nav-projects",
        label: "Go to Projects",
        category: "navigate",
        icon: <ProjectsIcon fontSize="small" />,
        action: () => {
          void navigate({ to: "/projects" });
          close();
        },
        keywords: ["projects", "pcbs", "boards"],
      },
      {
        id: "nav-history",
        label: "Go to History",
        category: "navigate",
        icon: <HistoryIcon fontSize="small" />,
        action: () => {
          void navigate({ to: "/history" });
          close();
        },
        keywords: ["history", "past", "previous"],
      },
      {
        id: "nav-settings",
        label: "Go to Settings",
        category: "navigate",
        icon: <SettingsIcon fontSize="small" />,
        action: () => {
          void navigate({ to: "/settings/profile" });
          close();
        },
        keywords: ["settings", "preferences", "account"],
      },
      {
        id: "nav-profile",
        label: "Edit your profile",
        category: "navigate",
        icon: <PersonIcon fontSize="small" />,
        action: () => {
          void navigate({ to: "/settings/profile" });
          close();
        },
        keywords: ["profile", "account", "name", "display", "user", "me"],
      },
    );

    // ── Actions ──────────────────────────────────────────────────────
    cmds.push(
      {
        id: "action-new-chat",
        label: "New chat",
        category: "action",
        icon: <NewChatIcon fontSize="small" />,
        action: () => {
          void navigate({ to: "/chat" });
          close();
        },
        keywords: ["new", "fresh", "start"],
      },
      {
        id: "action-upload",
        label: "Upload netlist",
        category: "action",
        icon: <UploadIcon fontSize="small" />,
        action: () => {
          void navigate({ to: "/projects" });
          // The projects page renders the Upload dialog when its tab
          // is on PCBs; a follow-up could thread `?upload=open` to
          // auto-open it.
          close();
        },
        keywords: ["upload", "import", "kicad", "easyeda", "netlist"],
      },
      {
        id: "action-open-drafts",
        label: activeSnapshotId ? "Open active draft" : "Open drafts panel",
        hint: activeSnapshotId
          ? `snapshot ${activeSnapshotId.slice(0, 8)}…`
          : undefined,
        category: "action",
        icon: <DraftIcon fontSize="small" />,
        action: () => {
          // openPanel requires a snapshot id; fall back to a bare
          // navigation if there's no active snapshot.
          if (activeSnapshotId) {
            openDraftPanel(activeSnapshotId);
          } else {
            void navigate({ to: "/chat" });
          }
          close();
        },
        keywords: ["draft", "drafts", "snapshot", "iteration", "panel"],
      },
      {
        id: "action-billing",
        label: "Manage billing",
        category: "action",
        icon: <BoltIcon fontSize="small" />,
        action: () => {
          void navigate({ to: "/settings/billing" });
          close();
        },
        keywords: ["billing", "credits", "wallet", "topup", "plan"],
      },
      {
        id: "action-api-keys",
        label: "Manage API keys",
        category: "action",
        icon: <SettingsIcon fontSize="small" />,
        action: () => {
          void navigate({ to: "/settings/api-keys" });
          close();
        },
        keywords: ["api", "keys", "kicad", "plugin", "token"],
      },
      {
        id: "action-shortcuts",
        label: "Show keyboard shortcuts",
        shortcut: "?",
        category: "action",
        icon: <KeyboardIcon fontSize="small" />,
        action: () => {
          openShortcuts();
          close();
        },
        keywords: ["shortcuts", "keyboard", "help", "hotkeys"],
      },
      {
        id: "action-logout",
        label: "Sign out",
        category: "action",
        icon: <LogoutIcon fontSize="small" />,
        action: () => {
          // Use a hard window navigation — clearing auth cookies +
          // bouncing through the backend logout is the cleanest path
          // and matches the SidebarUser sign-out gesture.
          window.location.href = "/auth/google/logout";
        },
        keywords: ["logout", "signout", "leave", "exit"],
      },
    );

    // ── Projects ─────────────────────────────────────────────────────
    if (projects && projects.length > 0) {
      // Most-recently-updated first; cap to 12 to keep the list scannable.
      const sorted = [...projects].sort((a, b) => {
        const ta = a.updated_at ? new Date(a.updated_at).getTime() : 0;
        const tb = b.updated_at ? new Date(b.updated_at).getTime() : 0;
        return tb - ta;
      });
      for (const p of sorted.slice(0, 12)) {
        if (!p.id) continue;
        const pid = p.id;
        cmds.push({
          id: `project-${pid}`,
          label: `Open ${p.name}`,
          hint:
            p.boards?.[0]?.current_version_semver?.trim() ||
            (p.boards?.[0]?.current_version_number != null
              ? `v${p.boards[0].current_version_number}`
              : undefined),
          category: "project",
          icon: <ProjectsIcon fontSize="small" />,
          action: () => {
            void navigate({
              to: "/projects/$projectId/overview",
              params: { projectId: pid },
            });
            close();
          },
          keywords: ["project", "pcb", "open", p.name],
        });
      }
    }

    // ── Recent sessions ──────────────────────────────────────────────
    // Skip surface-pinned sessions — those are owned by the project's
    // Workbench / Requirements rails, and the palette's "go to /chat
    // with this session" action would open them on the wrong surface
    // (driving the wrong agent on the next turn).
    if (sessions && sessions.length > 0) {
      const visible = sessions.filter((s) => !s.surface).slice(0, 8);
      for (const s of visible) {
        cmds.push({
          id: `session-${s.session_id}`,
          label: s.title || "(untitled chat)",
          hint: formatRelative(s.last_active),
          category: "recent",
          icon: <ChatIcon fontSize="small" />,
          action: () => {
            void navigate({
              to: "/chat",
              search: { s: s.session_id },
            });
            close();
          },
          keywords: ["session", "chat", "conversation", s.title ?? ""],
        });
      }
    }

    return cmds;
  }, [navigate, onClose, projects, sessions, activeSnapshotId, openDraftPanel]);
}

function formatRelative(iso: string | null | undefined): string {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "";
  const seconds = Math.floor((Date.now() - t) / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}
